import axios from 'axios';
import Cookies from 'js-cookie';

const axiosInstance = axios.create({
  // Base should point to the Django API root
  // Use Render backend by default; can override via REACT_APP_BACKEND_URL for local dev
  //baseURL: process.env.REACT_APP_BACKEND_URL || baseURL: "https://novarsis.ai/api/",
  //  baseURL: 'http://localhost:8000/api/',
  baseURL: process.env.REACT_APP_BACKEND_URL || "https://novarsis.ai/api/",
  withCredentials: true, // send cookies (useful if backend uses cookie refresh tokens)
  // You can also set other default configurations here
});

// Request interceptor to attach the token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    // Always attach token if present (2FA endpoints require auth)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Store reference to trigger auth failure from interceptor
let authFailureCallback = null;

export const setAuthFailureCallback = (callback) => {
  authFailureCallback = callback;
};

// Response interceptor to handle auth failures
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      const status = error.response.status;
      const originalRequest = error.config;

      if (status === 401) {
        // Only treat 401 as authentication failure
        console.error('🔥 AUTH ERROR - Status:', status, 'URL:', error.config?.url, 'Data:', error.response?.data);

        // Clear auth data immediately
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('account');

        // Trigger global auth failure handler if available
        if (authFailureCallback) {
          authFailureCallback('Session expired. Please sign in again.');
        }

        return Promise.reject(error);
      }

      if (status === 403) {
        // Check if this is a Firebase token expiration error
        const errorDetail = error.response?.data?.detail || '';
        
        if (errorDetail.includes('Firebase token') && errorDetail.includes('expired')) {
          console.log('🔄 Firebase token expired, attempting to refresh...');
          
          // Prevent infinite retry loop
          if (!originalRequest._retry) {
            originalRequest._retry = true;
            
            try {
              // Dynamically import to avoid circular dependencies
              const { refreshToken } = await import('../utils/tokenManagement');
              const newToken = await refreshToken();
              
              if (newToken) {
                console.log('✅ Firebase token refreshed successfully');
                // Update the Authorization header with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                // Retry the original request
                return axiosInstance(originalRequest);
              }
            } catch (refreshError) {
              console.error('❌ Firebase token refresh failed:', refreshError);
              // If refresh fails, clear auth and redirect
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              localStorage.removeItem('account');
              
              if (authFailureCallback) {
                authFailureCallback('Session expired. Please sign in again.');
              }
              return Promise.reject(error);
            }
          }
        }
        
        // Do NOT clear auth data on other 403 errors (business rule violations, permissions, etc.)
        // Let calling code handle it. Keep log for debugging.
        console.warn('🚫 Access denied (403) - URL:', error.config?.url, 'Data:', error.response?.data);
        return Promise.reject(error);
      }
      return Promise.reject(error);
    } else {
      // Handle case where error.response is not defined
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;
